/**
 * =============================================================================
 * «إعدادات عامة» — the public site's feature switches, as the CMS knows them
 * =============================================================================
 *
 * WHAT THIS CHANGES. Every switch below is a BUILD-TIME environment variable
 * on the website today — `REACT_APP_FEATURE_ANNOUNCEMENTS`,
 * `REACT_APP_CHAT_ENABLED`, and for the home-page sections, nothing at all
 * (the list is hard-coded in `DPA-FE/src/features/homePage/factory/
 * HomePageSections.jsx`). Turning one off means editing a file, rebuilding and
 * publishing to IIS by hand. The point of this screen is to make that a
 * switch an operator presses.
 *
 * -----------------------------------------------------------------------------
 * WHO OWNS WHAT, AND WHY IT IS SPLIT THIS WAY
 *
 * The BACKEND stores `key -> isEnabled` and nothing else.
 * This FILE owns the label, the description, the grouping and the order.
 *
 * Labels are Arabic CMS copy. Putting them in the database means a DBA for a
 * typo, a migration to reword a hint, and two sources of truth the day they
 * disagree. The key is the contract; everything else is presentation.
 *
 * It also has to survive both sides drifting, and both directions are handled
 * in `GeneralSettings.jsx` rather than hidden:
 *
 *   a key the API returns and this file does not know
 *       -> shown with its raw key and a note. Never hidden: an operator must
 *          be able to see every switch that is actually live.
 *   a key this file knows and the API does not return
 *       -> shown as "not configured yet", with its default. The website falls
 *          back to the same default, so the screen and the site agree.
 *
 * -----------------------------------------------------------------------------
 * `defaultEnabled` IS THE FAIL-SAFE, AND IT MATTERS MORE THAN IT LOOKS.
 *
 * It is what the WEBSITE must use when the settings call fails — a public site
 * that hides half its home page because one API request timed out is a far
 * worse outage than the one it was trying to avoid. So every section defaults
 * to VISIBLE, and the two features that are off in production today
 * (`chatbot`) default to OFF, which reproduces exactly today's behaviour when
 * the endpoint is unreachable.
 *
 * -----------------------------------------------------------------------------
 * ⚠ THE KEYS ARE A CONTRACT WITH THE WEBSITE. Renaming one silently turns the
 * switch back on for every visitor, because the site will not find it and will
 * fall back to `defaultEnabled`. Add new keys; never rename a live one.
 *
 * -----------------------------------------------------------------------------
 * ONLY IMPLEMENTED KEYS LIVE IN THIS LIST.
 *
 * The backend shipped three first (2026-11-03): the happiness meter, the AiX
 * chatbot and the «سجل اهتمامك» button. The other eleven are agreed, specced
 * and commented out at the bottom of `SITE_FEATURES` until they are built.
 *
 * That is the invariant: **everything in this list is accepted by the
 * service.** A key that is not would be rejected on save with
 * `FEATURE_KEY_UNKNOWN`, and a control that cannot work is worse than one that
 * is not offered - an operator flips it, saves, reads an error about keys, and
 * cannot tell whether the site changed.
 *
 * So uncommenting a row is not the whole job: confirm the backend accepts that
 * key first. Nothing guards this automatically, deliberately - a flag saying
 * "this one does not work yet" would be a way of keeping dead rows in the
 * list, which is exactly what was removed.
 * =============================================================================
 */

/** Groups, in the order they are rendered. */
export const FEATURE_GROUPS = [
  {
    key: 'homeSections',
    label: 'أقسام الصفحة الرئيسية',
    hint: 'إخفاء القسم يزيله من الصفحة الرئيسية للزوار فوراً. لا يُحذف أي محتوى.',
  },
  {
    key: 'homeElements',
    label: 'عناصر داخل الصفحة الرئيسية',
    hint: 'عناصر مفردة داخل قسم، لا القسم كله.',
  },
  {
    key: 'siteFeatures',
    label: 'خدمات عامة في الموقع',
    hint: 'تظهر في كل صفحات الموقع، لا في الصفحة الرئيسية وحدها.',
  },
  {
    key: 'structure',
    label: 'هيكل الصفحة',
    hint:
      '⚠ هذه ليست أقساماً عادية. إخفاء الواجهة أو التذييل يترك الصفحة بلا مدخل ' +
      'وبلا روابط تواصل — لا تُخفِها إلا لسبب مؤقت وواضح.',
  },
];

/**
 * Every switch, keyed by the string the website and the API both use.
 *
 * `sectionId` ties a home-page switch to the anchor the website already has in
 * `sectionIds` (`#latest`, `#degrees` …). It is carried here so the website's
 * renderer can map a key to a section without a second lookup table — one list
 * to keep in step instead of two.
 */
export const SITE_FEATURES = [
  /* ------------------------------------- single elements inside a section */
  {
    key: 'home.degrees.registerInterestButton',
    group: 'homeElements',
    label: 'زر «سجل اهتمامك» في قسم البرامج',
    description:
      'يخفي الزر وحده؛ قسم البرامج يبقى كما هو. الخدمة نفسها تظل متاحة من القائمة.',
    defaultEnabled: true,
  },

  /* ------------------------------------------------- site-wide services -- */
  {
    key: 'feature.happinessMeter',
    group: 'siteFeatures',
    label: 'مؤشر السعادة',
    description: 'نافذة تقييم رضا الزائر.',
    defaultEnabled: true,
  },
  {
    key: 'feature.chatbot',
    group: 'siteFeatures',
    label: 'المساعد الذكي (AiX)',
    description:
      'مُطفأ اليوم على الموقع المباشر. تشغيله من هنا يُظهره لكل زائر — ' +
      'لا تُشغّله قبل اعتماد إدارة الذكاء الاصطناعي وموقف حماية البيانات.',
    // Matches `REACT_APP_CHAT_ENABLED=false` in DPA-FE/.env.production, so an
    // unreachable settings call leaves the site exactly as it is today.
    defaultEnabled: false,
  },

  /* ===========================================================================
   * COMMENTED OUT 2026-11-03, waiting on the backend.
   *
   * The backend is implementing three keys first. These eleven were agreed and
   * specced (docs/APICall/SPEC-site-feature-toggles.md) but are NOT built, so
   * a save would come back `FEATURE_KEY_UNKNOWN`.
   *
   * Commented out rather than rendered read-only, by request: a switch an
   * operator cannot use is clutter on a settings screen, and a greyed row
   * invites "why can't I press this" on every visit.
   *
   * THE WEBSITE IS ALREADY WIRED FOR ALL OF THEM. DPA-FE carries every key in
   * its own `src/app/config/siteFeatures.js` with its default, so a key the
   * API does not send resolves to the state the site is in today. Nothing on
   * the site changes while these are commented out, and nothing on the site
   * needs changing when they come back.
   *
   * TO RESTORE one: uncomment its block - and CONFIRM THE BACKEND ACCEPTS THE
   * KEY FIRST. The invariant is now "everything in this list is implemented
   * server-side", and nothing enforces it automatically.
   *
   * The `homeSections` and `structure` groups are kept in `FEATURE_GROUPS` for
   * the same reason: they are the homes these rows come back to.
   * ===========================================================================
   *
   * // ---- home-page sections ----
   * {
   *   key: 'home.section.latest',
   *   group: 'homeSections',
   *   sectionId: 'latest',
   *   label: 'المركز الإعلامي',
   *   defaultEnabled: true,
   * },
   * {
   *   key: 'home.section.degrees',
   *   group: 'homeSections',
   *   sectionId: 'degrees',
   *   label: 'البرامج',
   *   defaultEnabled: true,
   * },
   * {
   *   key: 'home.section.campusLife',
   *   group: 'homeSections',
   *   sectionId: 'campus-life',
   *   label: 'الحياة الجامعية',
   *   defaultEnabled: true,
   * },
   * {
   *   key: 'home.section.aboutTheAcademy',
   *   group: 'homeSections',
   *   sectionId: 'about-the-academy',
   *   label: 'عن الأكاديمية',
   *   defaultEnabled: true,
   * },
   * {
   *   key: 'home.section.universalMap',
   *   group: 'homeSections',
   *   sectionId: 'universal-map',
   *   label: 'الخريطة العالمية',
   *   defaultEnabled: true,
   * },
   * {
   *   key: 'home.section.academyInNumber',
   *   group: 'homeSections',
   *   sectionId: 'academy-in-number',
   *   label: 'الأكاديمية بالأرقام',
   *   defaultEnabled: true,
   * },
   * {
   *   key: 'home.section.testimonials',
   *   group: 'homeSections',
   *   sectionId: 'testimonials',
   *   label: 'شهادات الطلبة والخريجين',
   *   defaultEnabled: true,
   * },
   *
   * // ---- site-wide services ----
   * {
   *   key: 'feature.announcements',
   *   group: 'siteFeatures',
   *   label: 'نافذة الإعلانات المنبثقة',
   *   defaultEnabled: true,
   * },
   * {
   *   key: 'feature.visitorCounter',
   *   group: 'siteFeatures',
   *   label: 'عدّاد الزوار في التذييل',
   *   defaultEnabled: true,
   * },
   *
   * // ---- structure: NOT ordinary sections ----
   * {
   *   key: 'home.section.home',
   *   group: 'structure',
   *   sectionId: 'home',
   *   label: 'واجهة الصفحة الرئيسية (السلايدر)',
   *   defaultEnabled: true,
   * },
   * {
   *   key: 'home.section.footer',
   *   group: 'structure',
   *   sectionId: 'footer',
   *   label: 'التذييل',
   *   defaultEnabled: true,
   * },
   * ========================================================================= */
];

/** The catalogue as a map, for the merge in `GeneralSettings`. */
export const FEATURE_BY_KEY = SITE_FEATURES.reduce((map, feature) => {
  map[feature.key] = feature;
  return map;
}, {});

/** What the site falls back to when the settings call fails. */
export const DEFAULT_FEATURE_STATE = SITE_FEATURES.reduce((map, feature) => {
  map[feature.key] = feature.defaultEnabled;
  return map;
}, {});

/**
 * Merge what the API returned with what this file knows.
 *
 * One row per CATALOGUE switch, in catalogue order. A key the API sends that
 * this file does not list is IGNORED.
 *
 * ⚠ THAT IGNORING HAS A COST, recorded here rather than discovered later. If
 * the backend ships a switch before the CMS lists it, that switch acts on the
 * public site with no control anywhere in this interface — nobody can see it
 * or turn it off from here. An earlier version surfaced such keys in a
 * «مفاتيح غير معروفة» group for exactly that reason; it was removed by request
 * because it is clutter in the normal case, when there are none.
 *
 * The mitigation is process, not code: when the backend ships a key, add it to
 * `SITE_FEATURES` in the same change. Eleven are already written and commented
 * out at the bottom of that list, so for those it is uncommenting one block.
 *
 * @param {{key:string,isEnabled:boolean}[]} apiItems
 * @returns {object[]} rows with `{ ...meta, isEnabled, isConfigured }`
 */
export const mergeFeatures = (apiItems) => {
  const fromApi = new Map(
    (Array.isArray(apiItems) ? apiItems : []).map((item) => [item.key, item])
  );

  const known = SITE_FEATURES.map((feature) => {
    const row = fromApi.get(feature.key);
    return {
      ...feature,
      isEnabled: row ? Boolean(row.isEnabled) : feature.defaultEnabled,
      // Distinguishes "the operator turned it on" from "nobody has ever set
      // it, and this is the default" — they look identical in a checkbox and
      // are not the same fact.
      isConfigured: Boolean(row),
      updatedOn: row?.updatedOn ?? null,
      updatedBy: row?.updatedBy ?? null,
    };
  });

  return known;
};

/** Only what actually changed, for the save request and the confirm line. */
export const changedFeatures = (rows, draft) =>
  rows
    .filter((row) => draft[row.key] !== undefined && draft[row.key] !== row.isEnabled)
    .map((row) => ({ ...row, isEnabled: draft[row.key] }));

const siteFeatures = {
  FEATURE_GROUPS,
  SITE_FEATURES,
  FEATURE_BY_KEY,
  DEFAULT_FEATURE_STATE,
  mergeFeatures,
  changedFeatures,
};

export default siteFeatures;
